/**
 * Les articles d'une facture de grossiste (Sabag, Sanitas Troesch, Richner…),
 * où chaque ligne de chiffres porte un code de groupe de rabais :
 *
 *   10.00 146 026.103.183      1.000 pce   S001   434.00     434.00  U1
 *         Receveur de douche Pro, blanc mat, standard, 90 x
 *
 * La désignation est écrite sous les chiffres, et le rabais de chaque groupe
 * n'est donné qu'à la fin, dans la « Récapitulation net » (« S001 Général …
 * Rabais 25% »). Sans ce lecteur, les quatre lignes du récapitulatif étaient
 * prises pour les articles, au prix brut.
 */

const UNITE =
  /pce|pcs|pi[eè]ces?|stk|st|m|m2|m²|ml|lm|kg|l|sacs?|rlx?|btes?|ctn|pal|paq|h|u|ens|set|lots?/
    .source;

const NOMBRE = "\\d{1,3}(?:['’]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?";

/** Position, n° d'article, quantité, unité, groupe, prix, [rabais %], montant, [code TVA]. */
const LIGNE = new RegExp(
  '^\\s*\\d+(?:[.,]\\d+)?\\s+(\\d[\\d .\\-/]*?)\\s{2,}(' +
    NOMBRE +
    ')\\s+(' +
    UNITE +
    ')\\s+(?:([A-Z]{1,3}\\d{2,4})\\s+)?(' +
    NOMBRE +
    ')\\s+(?:(\\d+(?:[.,]\\d+)?)\\s*%\\s+)?(' +
    NOMBRE +
    ')(?:\\s+[A-Z]\\d)?\\s*$',
  'i',
);

const RABAIS = /\b(rabais|rabatt|sconto|remise|discount)\s+(\d+(?:[.,]\d+)?)\s*%/i;

const nombre = (s) => Number(String(s).replace(/['’]/g, '').replace(',', '.'));

/** Vrai si a et b se valent, à un centime ou un pour cent près. */
const egal = (a, b) => Math.abs(a - b) <= Math.max(0.05, Math.abs(b) * 0.01);

/**
 * Le nom court d'un article : sa première ligne de description, jusqu'à la
 * première virgule (« Receveur de douche Pro »).
 */
function nomCourt(lignes) {
  const premiere = (lignes[0] || '').trim();
  const virgule = premiere.indexOf(', ');
  const nom = virgule >= 8 ? premiere.slice(0, virgule) : premiere;
  return nom.replace(/[\s,;:-]+$/, '').slice(0, 80);
}

/**
 * @param {string} texte texte d'un PDF, colonnes gardées
 * @returns {{designation: string, quantite: number, unite: string,
 *   prixUnitaire: number}[]} les articles, au prix net du
 *   rabais de leur groupe ; vide si la facture n'a pas cette forme
 */
export function lignesDeGrossiste(texte) {
  const lignes = String(texte || '').split(/\r?\n/);
  const propres = lignes.map((l) => l.trim());
  // L'en-tête et le pied de chaque page reviennent : ils ne décrivent rien.
  const vus = new Map();
  for (const l of propres) if (l) vus.set(l, (vus.get(l) || 0) + 1);
  const repetee = (l) => (vus.get(l) || 0) >= 2;

  const articles = [];
  for (let i = 0; i < lignes.length; i += 1) {
    const m = lignes[i].match(LIGNE);
    if (!m) continue;
    const [, , q, u, groupe, p, rem, montant] = m;
    const quantite = nombre(q);
    const prix = nombre(p);
    const remise = rem ? nombre(rem) : 0;
    if (!(quantite > 0) || !egal(quantite * prix * (1 - remise / 100), nombre(montant))) continue;
    const description = [];
    for (let j = i + 1; j < lignes.length && description.length < 6; j += 1) {
      const l = propres[j];
      if (!l || LIGNE.test(lignes[j]) || /^[-=_*]{4,}$/.test(l)) break;
      // Une ligne répétée est un pied de page, sauf juste sous les chiffres :
      // deux articles pareils (« Set de fixation murale DUOFIX ») ont la même.
      if (repetee(l) && j > i + 1) break;
      if (/\bpage\s+\d+\s*\/\s*\d+/i.test(l) || /^pos\.?\s/i.test(l)) break;
      // Un titre de section, souligné à la ligne suivante.
      if (/^[-=_]{4,}$/.test(propres[j + 1] || '')) break;
      description.push(l);
    }
    if (!description.length) continue;
    articles.push({
      groupe: groupe || null,
      designation: nomCourt(description),
      quantite,
      unite: u.toLowerCase(),
      prix: remise ? Math.round(prix * (1 - remise / 100) * 100) / 100 : prix,
    });
  }
  if (articles.length < 2) return [];

  // Le rabais de chaque groupe, donné au récapitulatif.
  const groupes = new Set(articles.map((a) => a.groupe).filter(Boolean));
  const rabais = new Map();
  for (let i = 0; i < propres.length; i += 1) {
    const g = (propres[i].match(/^([A-Z]{1,3}\d{2,4})\b/) || [])[1];
    if (!g || !groupes.has(g) || rabais.has(g)) continue;
    for (let j = i + 1; j <= i + 3 && j < propres.length; j += 1) {
      if (/^[A-Z]{1,3}\d{2,4}\b/.test(propres[j])) break;
      const r = propres[j].match(RABAIS);
      if (r) {
        rabais.set(g, nombre(r[2]));
        break;
      }
    }
  }
  return articles.map((a) => {
    const r = a.groupe ? rabais.get(a.groupe) || 0 : 0;
    return {
      designation: a.designation,
      quantite: a.quantite,
      unite: a.unite,
      prixUnitaire: r ? Math.round(a.prix * (1 - r / 100) * 100) / 100 : a.prix,
    };
  });
}
